import { collection, query, where, getDocs, addDoc, deleteDoc, doc, Timestamp, getDoc, updateDoc, writeBatch, type DocumentData, type FirestoreError } from 'firebase/firestore';
import { db } from '../config';
import { handleFirebaseError } from '../error-handling';
import { CHUNK_CONCURRENCY, chunkIds, forEachLimited } from './batchedIn';
import { isOrganizationManager } from '../queries/organizations/isOrganizationMember';
import {
  checkGroupMembershipGuard,
  SectionNotJoinableError,
  SubgroupConflictError,
  type GuardGroup,
  type ExistingMembership,
} from './groupMembershipGuard';

export { SectionNotJoinableError, SubgroupConflictError };

/**
 * Settled product rule: sections (`groupType === 'parent'`) are conceptual
 * and not directly joinable, and a user may belong to at most one subgroup
 * per organization (org-less groups are unconstrained). Every writer of a
 * `groupMembers` row must call this before adding one. Throws
 * `SectionNotJoinableError` / `SubgroupConflictError` — both catchable, and
 * distinct from generic Firestore failures.
 */
export async function assertMembershipAllowed(
  groupId: string,
  userId: string,
  // A membership the caller is removing in the same write (moveGroupMember),
  // so it does not count as the conflicting one.
  ignoreGroupId?: string,
): Promise<void> {
  const groupSnap = await getDoc(doc(db, 'supportGroups', groupId));
  if (!groupSnap.exists()) {
    throw new Error('Group not found');
  }
  const groupData = groupSnap.data() as any;
  const targetGroup: GuardGroup = {
    id: groupId,
    groupType: groupData.groupType,
    organization: groupData.organization,
  };

  let existingMemberships: ExistingMembership[] = [];
  if (targetGroup.organization && targetGroup.groupType !== 'parent') {
    const otherMembershipsSnapshot = await getDocs(
      query(collection(db, 'groupMembers'), where('userId', '==', userId)),
    );
    const otherGroupIds = Array.from(
      new Set(
        otherMembershipsSnapshot.docs
          .map((d) => d.data().groupId as string)
          .filter((gid) => gid !== groupId && gid !== ignoreGroupId),
      ),
    );
    existingMemberships = await Promise.all(
      otherGroupIds.map(async (gid) => {
        const gSnap = await getDoc(doc(db, 'supportGroups', gid));
        const gData = gSnap.exists() ? (gSnap.data() as any) : {};
        return {
          groupId: gid,
          group: { id: gid, groupType: gData.groupType, organization: gData.organization },
        } as ExistingMembership;
      }),
    );
  }

  const result = checkGroupMembershipGuard(targetGroup, existingMemberships);
  if (!result.ok) {
    if (result.reason === 'section-not-joinable') {
      throw new SectionNotJoinableError(groupId);
    }
    throw new SubgroupConflictError(result.existingGroupId, groupId);
  }
}

export async function joinGroup(groupId: string, userId: string) {
  try {
    // Check if already a member
    const membershipQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId),
      where('userId', '==', userId)
    );

    const existingMembership = await getDocs(membershipQuery);
    if (!existingMembership.empty) {
      return true; // Already a member
    }

    await assertMembershipAllowed(groupId, userId);

    // Add new membership
    // Explicit role, as mobile writes it (readers defaulted a missing one).
    await addDoc(collection(db, 'groupMembers'), {
      groupId,
      userId,
      role: 'member',
      joinedAt: Timestamp.now()
    });

    // ❌ DISABLED: No propagation - using pure virtual membership system

    return true;
  } catch (error) {
    if (error instanceof SectionNotJoinableError || error instanceof SubgroupConflictError) {
      throw error;
    }
    handleFirebaseError(error);
    return false;
  }
}

export async function leaveGroup(groupId: string, userId: string) {
  try {
    const membershipQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId),
      where('userId', '==', userId)
    );
    
    const membershipDocs = await getDocs(membershipQuery);
    
    if (membershipDocs.empty) {
      return true; // Not a member anyway
    }

    // Remove membership
    await Promise.all(
      membershipDocs.docs.map(doc => deleteDoc(doc.ref))
    );

    return true;
  } catch (error) {
    handleFirebaseError(error);
    return false;
  }
}

export async function getGroupMembers(groupId: string) {
  try {
    const membersQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId)
    );
    
    const membersSnapshot = await getDocs(membersQuery);
    
    // Get user details for all members
    const memberData = membersSnapshot.docs.map(doc => ({
      userId: doc.data().userId,
      role: doc.data().role || 'member'
    }));
    
    // Firestore `in` takes at most 30 values and rejects an empty list (#36):
    // read users 30 at a time on the `id` field (as getOrganizationMembers
    // does, #117), none for an empty group. A users doc id may differ from its
    // `id` field and some ids have duplicate docs, so match on the field and
    // keep the first doc per id (document-id order, as Firestore returns it).
    const chunks = chunkIds(memberData.map(member => member.userId));
    if (chunks.length === 0) return [];
    const usersById = new Map<string, { docId: string; data: DocumentData }>();
    await forEachLimited(chunks, CHUNK_CONCURRENCY, async (part) => {
      const snapshot = await getDocs(query(collection(db, 'users'), where('id', 'in', part)));
      snapshot.docs.forEach((d) => {
        const data = d.data();
        if (typeof data.id === 'string' && !usersById.has(data.id)) {
          usersById.set(data.id, { docId: d.id, data });
        }
      });
    });
    
    // Combine user data with role information (one row per member, in member order)
    const roleByUser = new Map(memberData.map(m => [m.userId, m.role]));
    return Array.from(roleByUser.keys()).flatMap(userId => {
      const user = usersById.get(userId);
      if (!user) return [];
      return [{
        id: user.docId,
        ...user.data,
        role: roleByUser.get(userId) || 'member'
      }];
    });
  } catch (error) {
    handleFirebaseError(error);
    return [];
  }
}

export async function isGroupMember(groupId: string, userId: string): Promise<boolean> {
  try {
    const membershipQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId),
      where('userId', '==', userId)
    );
    
    const membershipDocs = await getDocs(membershipQuery);
    return !membershipDocs.empty;
  } catch (error) {
    handleFirebaseError(error);
    return false;
  }
}

export async function isGroupEditor(groupId: string, userId: string): Promise<boolean> {
  try {
    // First check if user is the creator of the group
    const groupRef = doc(db, 'supportGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (groupDoc.exists()) {
      const groupData = groupDoc.data();
      if (groupData.creatorId === userId) {
        console.log(`✅ User ${userId} is the creator of group ${groupId}`);
        return true;
      }
    }
    
    // Then check if user has editor role in groupMembers
    const membershipQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId),
      where('userId', '==', userId),
      where('role', '==', 'editor')
    );
    
    const membershipDocs = await getDocs(membershipQuery);
    const isEditor = !membershipDocs.empty;
    
    if (isEditor) {
      console.log(`✅ User ${userId} is an editor of group ${groupId}`);
    }
    
    return isEditor;
  } catch (error) {
    console.error(`❌ Error checking editor status for user ${userId} in group ${groupId}:`, error);
    handleFirebaseError(error);
    return false;
  }
}

export async function addGroupMember(groupId: string, userId: string, addedByUserId: string): Promise<boolean> {
  try {
    // Get group information once for all validations
    const groupRef = doc(db, 'supportGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }
    
    const groupData = groupDoc.data();
    
    // ✅ Now sections are fully functional groups that can have members
    // Members are automatically propagated from child groups, but can also be added directly
    
    // Check if the user adding the member is an editor or the creator
    const isEditor = await isGroupEditor(groupId, addedByUserId);
    const isCreator = groupData.creatorId === addedByUserId;
    
    // If not an editor or creator, check if they're an organization manager
    let hasPermission = isEditor || isCreator;
    if (!hasPermission && groupData.organization) {
      // Check if user is a manager of this organization
      hasPermission = await isOrganizationManager(groupData.organization, addedByUserId);
    }
    
    if (!hasPermission) {
      throw new Error('Only editors, creators, or organization managers can add members');
    }

    // Check if already a member
    const membershipQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId),
      where('userId', '==', userId)
    );

    const existingMembership = await getDocs(membershipQuery);
    if (!existingMembership.empty) {
      return true; // Already a member
    }

    await assertMembershipAllowed(groupId, userId);

    // Add new membership
    await addDoc(collection(db, 'groupMembers'), {
      groupId,
      userId,
      role: 'member',
      joinedAt: Timestamp.now(),
      addedBy: addedByUserId
    });

    // ✅ Pure operation - no sync needed, membership will be calculated dynamically
    console.log(`✅ Added member ${userId} to group ${groupId} - parent sections will reflect this automatically`);

    return true;
  } catch (error) {
    if (error instanceof SectionNotJoinableError || error instanceof SubgroupConflictError) {
      throw error;
    }
    handleFirebaseError(error);
    return false;
  }
}

/** A support group's display name, or its id when it cannot be read. */
export async function getGroupName(groupId: string): Promise<string> {
  try {
    const snap = await getDoc(doc(db, 'supportGroups', groupId));
    const name = snap.exists() ? (snap.data() as { name?: unknown }).name : undefined;
    return typeof name === 'string' && name ? name : groupId;
  } catch {
    return groupId;
  }
}

/**
 * namat#30: a user belongs to one group per organization, so placing them in
 * another group of the same organization is a MOVE. The old membership is
 * removed and the new one added in ONE writeBatch, so a failure never leaves
 * the user in neither group (or both), and the server's one-group-per-org
 * trigger never sees the two rows side by side. The member keeps their role
 * (an editor stays an editor); addedBy is the acting user. false when either
 * side is refused (same permissions as removeGroupMember / addGroupMember).
 */
export async function moveGroupMember(
  fromGroupId: string,
  toGroupId: string,
  userId: string,
  movedByUserId: string,
): Promise<boolean> {
  try {
    const [fromDoc, toDoc] = await Promise.all([
      getDoc(doc(db, 'supportGroups', fromGroupId)),
      getDoc(doc(db, 'supportGroups', toGroupId)),
    ]);
    if (!fromDoc.exists() || !toDoc.exists()) {
      throw new Error('Group not found');
    }
    const fromData = fromDoc.data();
    const toData = toDoc.data();

    // Same protection as removeGroupMember.
    if (fromData.organization && await isOrganizationManager(fromData.organization, userId)) {
      throw new Error('Organization managers cannot be removed from groups');
    }

    // Same permission as removeGroupMember (from) and addGroupMember (to).
    const canManage = async (groupId: string, groupData: DocumentData) =>
      groupData.creatorId === movedByUserId ||
      await isGroupEditor(groupId, movedByUserId) ||
      (!!groupData.organization && await isOrganizationManager(groupData.organization, movedByUserId));
    if (!(await canManage(fromGroupId, fromData)) || !(await canManage(toGroupId, toData))) {
      throw new Error('Only editors, creators, or organization managers can move members');
    }

    const [oldRows, targetRows] = await Promise.all([
      getDocs(query(collection(db, 'groupMembers'), where('groupId', '==', fromGroupId), where('userId', '==', userId))),
      getDocs(query(collection(db, 'groupMembers'), where('groupId', '==', toGroupId), where('userId', '==', userId))),
    ]);

    await assertMembershipAllowed(toGroupId, userId, fromGroupId);

    const batch = writeBatch(db);
    oldRows.docs.forEach(d => batch.delete(d.ref));
    if (targetRows.empty) {
      const role = oldRows.docs.map(d => d.data().role).find(r => typeof r === 'string' && r) || 'member';
      batch.set(doc(collection(db, 'groupMembers')), {
        groupId: toGroupId,
        userId,
        role,
        joinedAt: Timestamp.now(),
        addedBy: movedByUserId
      });
    }
    await batch.commit();

    console.log(`✅ Moved member ${userId} from group ${fromGroupId} to ${toGroupId}`);
    return true;
  } catch (error) {
    if (error instanceof SectionNotJoinableError || error instanceof SubgroupConflictError) {
      throw error;
    }
    handleFirebaseError(error as FirestoreError);
    return false;
  }
}

export async function removeGroupMember(groupId: string, userId: string, removedByUserId: string): Promise<boolean> {
  try {
    // Get group information first to check for organization
    const groupRef = doc(db, 'supportGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }
    
    const groupData = groupDoc.data();
    
    // 🛡️ PROTECTION: Check if the user being removed is an organization manager
    if (groupData.organization) {
      const isTargetOrgManager = await isOrganizationManager(groupData.organization, userId);
      if (isTargetOrgManager) {
        console.log('🛡️ Cannot remove organization manager from group');
        throw new Error('Organization managers cannot be removed from groups');
      }
    }

    // Check if the user removing the member is an editor or the creator
    const isEditor = await isGroupEditor(groupId, removedByUserId);
    const isCreator = groupData.creatorId === removedByUserId;
    
    // If not an editor or creator, check if they're an organization manager
    let hasPermission = isEditor || isCreator;
    if (!hasPermission) {
      if (groupData.organization) {
        // Check if user is a manager of this organization
        hasPermission = await isOrganizationManager(groupData.organization, removedByUserId);
      }
    }
    
    if (!hasPermission) {
      throw new Error('Only editors, creators, or organization managers can remove members');
    }

    const membershipQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId),
      where('userId', '==', userId)
    );
    
    const membershipDocs = await getDocs(membershipQuery);
    
    if (membershipDocs.empty) {
      return true; // Not a member anyway
    }

    // Remove membership
    await Promise.all(
      membershipDocs.docs.map(doc => deleteDoc(doc.ref))
    );

    // ✅ Pure operation - no sync needed, membership will be calculated dynamically
    console.log(`✅ Removed member ${userId} from group ${groupId} - parent sections will reflect this automatically`);

    return true;
  } catch (error) {
    handleFirebaseError(error);
    return false;
  }
}

